import { takeUntil, tap } from 'rxjs/operators'
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import { NgOption, NgSelectComponent } from '@ng-select/ng-select'
import * as lodash from 'lodash'
import { MultiSelectOptionLabelDirective, MultiSelectSelectedLabelDirective } from './form-multi-select.directive'
import { FormFieldComponent } from '../form-field/form-field.component'

// Same as ng-select's CompareWithFn, which its public API does not export.
type CompareWithFn = (a: any, b: any) => boolean

@Component({
  selector: 'app-form-multi-select',
  templateUrl: './form-multi-select.component.html',
  styleUrls: ['../form-field-common.scss', './form-multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormMultiSelectComponent extends FormFieldComponent {
  @Input() items: any[]
  @Input() bindLabel: string
  @Input() bindValue: string
  @Input() searchPlaceholder = $localize`Search...`
  @Input() compareWith: CompareWithFn

  @Output() search = new EventEmitter<string>()

  @ViewChild('select', { static: true }) selectComponent: NgSelectComponent
  @ViewChild('customSearchInput', { static: true }) customSearchInput: ElementRef

  @ContentChild(MultiSelectOptionLabelDirective) selectOptionLabel: MultiSelectOptionLabelDirective
  @ContentChild(MultiSelectSelectedLabelDirective) selectSelectedLabel: MultiSelectSelectedLabelDirective

  searchCtrl = new UntypedFormControl()
  masterToggleCtrl = new UntypedFormControl()

  get selectedItems(): any[] {
    return (this.selectComponent && this.selectComponent.selectedItems) || []
  }

  get isMasterToggleIndeterminate(): boolean {
    return this.selectedItems.length && this.items && this.items.length > this.selectedItems.length
  }

  ngOnInit(): void {
    super.ngOnInit()

    this.searchCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((term) => {
      this.selectComponent.filter(term)
    })

    this.masterToggleCtrl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.onMasterToggle())
      )
      .subscribe()
  }

  onFocus() {
    setTimeout(() => {
      if (this.customSearchInput) {
        this.customSearchInput.nativeElement.focus()
        this.customSearchInput.nativeElement.select()
      }
    }, 100)
  }

  onMasterToggle() {
    if (this.masterToggleCtrl.value) {
      this.onSelectAll()
    } else {
      this.ctrl.reset([])
    }
  }

  onValueChange(data) {
    this.valueChange.emit(data)
    this.masterToggleCtrl.setValue(this.selectedItems.length === this.items.length, { emitEvent: false })
  }

  onSearch({ term }: { term: string }) {
    this.search.emit(term)
  }

  onClearSearch() {
    this.searchCtrl.reset()
  }

  onSelectDropdownOpen() {
    this.focused = true
    this.onFocus()
  }

  onSelectDropdownClose() {
    this.focused = false
    this.searchCtrl.reset('', { emitEvent: false })
  }

  onItemClick($event: MouseEvent, item: NgOption) {
    $event.stopPropagation()
    $event.preventDefault()
    this.selectComponent.toggleItem(item)
  }

  onSelectAll() {
    this.ctrl.patchValue(lodash.map(this.items, (item) => this.getItemValue(item)))
  }

  getSelectedLabels(selected: any[]): string {
    return lodash.map(selected, (item) => this.getItemLabel(item)).join(', ')
  }

  getItemLabel(item): string {
    return this.bindLabel ? lodash.get(item, this.bindLabel) : lodash.toString(item)
  }

  getItemValue(item) {
    return this.bindValue ? lodash.get(item, this.bindValue) : item
  }
}
